/**
 * AdditionalViewFormModal — Ustawienia → „Widoki dodatkowe" (F22, ADR-0087), modal create/edit.
 * Mirror `ProcessProcedureFormModal.tsx` (edytor SQL + edytor parametrów + sekcja „Testuj") z
 * dwiema różnicami:
 *  - dokłada picker kartoteki nadrzędnej + slug (jak `AdditionalTablesTab.tsx` → `CreateTableModal`),
 *    NIEEDYTOWALNE po utworzeniu (zmiana slugu/rodzica = zmiana tożsamości wiersza katalogu,
 *    partial-unique jest po `(parentTableName, slug)`),
 *  - dokłada edytor WIĄZAŃ parametrów (`DictionaryParamBindingsEditor` — reużyty wprost, ten sam
 *    model `record_field|context.company|context.user|constant` co słowniki SQL, ADR-0087
 *    Decyzja 4) — deklaracja parametru (nazwa/typ/wymagany) i jego wiązanie do źródła wartości są
 *    DWIEMA oddzielnymi rzeczami: deklaracja żyje w tym pliku (lista poniżej SQL), wiązanie w
 *    edytorze reużytym z katalogu słowników.
 *
 * Sekcja „Testuj" woła `POST /settings/additional-views/test-sql` z przykładowymi WARTOŚCIAMI
 * wpisanymi wprost przez autora (nie wiązaniami — w Ustawieniach nie ma jeszcze żadnego
 * konkretnego rekordu do związania), zwraca `AdditionalViewData` (widok jest zawsze SELECT-em,
 * bez dychotomii walidacja/akcja jak procedury kontrolne procesów).
 *
 * Pola-sąsiedzi do wiązania `record_field` pochodzą z `useFieldsForModule` (katalog pól
 * core+ext kartoteki wskazanej przez `parentEntity`) — z WYJĄTKIEM `parentEntity==='documents'`:
 * ten hook wymaga `documentClass` (widoki dokumentów są per-klasa: faktury/umowy/pisma/…), a
 * widok dodatkowy jest wspólny dla WSZYSTKICH 7 klas (mirror `ext_additional_tables`, Wariant A)
 * — nie ma jednej klasy do przekazania. Świadomy gap: dla widoków na kartotece „Dokumenty" lista
 * pól-sąsiadów jest pusta (autor nadal może użyć wiązań `context.company`/`context.user`/`constant`,
 * `record_field` wymaga wtedy ręcznego wpisania slugu — do czasu rozszerzenia `useFieldsForModule`
 * o tryb "wszystkie klasy" albo per-widok wyboru klasy, poza zakresem tego etapu).
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.confirm/alert — biblioteka Modal/Button/FormField.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdditionalViewParamBindings,
  AdditionalViewParamType,
  AdditionalViewSourceParam,
} from '@saas/shared';
import { isAxiosError } from 'axios';
import { Plus, Trash2 } from 'lucide-react';

import { formatCellValue } from '@/components/additional/AdditionalViewTab';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { DictionaryParamBindingsEditor } from '@/components/dictionaries/DictionaryParamBindingsEditor';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { InsertExampleButton } from '@/components/InsertExampleButton';
import { Modal } from '@/components/Modal';
import { useFieldsForModule } from '@/components/process-builder/useFieldsForModule';
import { Textarea } from '@/components/Textarea';
import { SqlField } from '@/components/ui/SqlField';
import { useTestAdditionalViewSql } from '@/lib/additional-views/api';
import { ADDITIONAL_VIEW_RESERVED_PARAMS, buildSqlParamCompletions } from '@/lib/sql-editor/params';
import { useTenantSqlSchema } from '@/lib/sql-editor/schema';
import { useSqlTestDiagnostics } from '@/lib/sql-editor/test-error';

const SLUG_REGEX = /^[a-z][a-z0-9_]{0,49}$/;
const PARAM_NAME_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

const ADDITIONAL_VIEW_PARAM_TYPES: readonly AdditionalViewParamType[] = [
  'text',
  'int',
  'decimal',
  'date',
  'bool',
] as const;

/** Whitelist kartotek systemowych jako rodziców widoków dodatkowych — mirror
 *  `SYSTEM_PARENT_OPTIONS` (`AdditionalTablesTab.tsx`); reużywa TE SAME klucze i18n
 *  (koncepcja „kartoteka nadrzędna" jest identyczna dla obu katalogów). */
interface ParentOption {
  labelKey: string;
  parentTableName: string;
  parentEntityType: 'system';
  parentEntity: string;
}

const SYSTEM_PARENT_OPTIONS: ParentOption[] = [
  {
    labelKey: 'additionalTables.admin.parentOptions.employees',
    parentTableName: 'employees',
    parentEntityType: 'system',
    parentEntity: 'employees',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.contractors',
    parentTableName: 'contractors',
    parentEntityType: 'system',
    parentEntity: 'contractors',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.companies',
    parentTableName: 'companies',
    parentEntityType: 'system',
    parentEntity: 'companies',
  },
  {
    // F24 Pakiet 0: fizyczna tabela `departments` → `org_units` (jednostki organizacyjne).
    labelKey: 'additionalTables.admin.parentOptions.orgUnits',
    parentTableName: 'org_units',
    parentEntityType: 'system',
    parentEntity: 'org_units',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.contractorContacts',
    parentTableName: 'contractor_contacts',
    parentEntityType: 'system',
    parentEntity: 'contractor_contacts',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.users',
    parentTableName: 'users',
    parentEntityType: 'system',
    parentEntity: 'users',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.documents',
    parentTableName: 'documents',
    parentEntityType: 'system',
    parentEntity: 'documents',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.tasks',
    parentTableName: 'tasks',
    parentEntityType: 'system',
    parentEntity: 'tasks',
  },
];

function nameToSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 50) || 'widok'
  );
}

export interface AdditionalViewFormValues {
  displayName: string;
  slug: string;
  parentTableName: string;
  parentEntityType: 'system' | 'custom';
  parentEntity: string;
  description: string;
  sourceSql: string;
  declaredParams: AdditionalViewSourceParam[];
  paramBindings: AdditionalViewParamBindings | null;
}

export function emptyAdditionalViewFormValues(): AdditionalViewFormValues {
  const first = SYSTEM_PARENT_OPTIONS[0]!;
  return {
    displayName: '',
    slug: '',
    parentTableName: first.parentTableName,
    parentEntityType: first.parentEntityType,
    parentEntity: first.parentEntity,
    description: '',
    sourceSql: '',
    declaredParams: [],
    paramBindings: null,
  };
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: AdditionalViewFormValues;
  saving?: boolean;
  serverErrors?: Record<string, string>;
  onSubmit: (values: AdditionalViewFormValues) => void;
  onClose: () => void;
}

export function AdditionalViewFormModal({
  open,
  mode,
  initialValues,
  saving,
  serverErrors,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(initialValues.displayName);
  const [slug, setSlug] = useState(initialValues.slug);
  const [slugTouched, setSlugTouched] = useState(mode === 'edit');
  const [parentEntity, setParentEntity] = useState(initialValues.parentEntity);
  const [description, setDescription] = useState(initialValues.description);
  const [sourceSql, setSourceSql] = useState(initialValues.sourceSql);
  const [declaredParams, setDeclaredParams] = useState<AdditionalViewSourceParam[]>(
    initialValues.declaredParams,
  );
  const [paramBindings, setParamBindings] = useState<AdditionalViewParamBindings | null>(
    initialValues.paramBindings,
  );
  const [testParamValues, setTestParamValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const testMutation = useTestAdditionalViewSql();
  const sqlSchema = useTenantSqlSchema(open);
  const sqlDiagnostics = useSqlTestDiagnostics(sourceSql, testMutation);
  const sqlParams = useMemo(
    () => buildSqlParamCompletions(t, ADDITIONAL_VIEW_RESERVED_PARAMS, declaredParams),
    [t, declaredParams],
  );

  // Katalog pól core+ext kartoteki wybranej jako rodzic — kandydaci do wiązania `record_field`.
  // `documents` jest świadomym wyjątkiem (patrz docstring pliku) — hook wymaga `documentClass`,
  // którego widok wspólny dla 7 klas nie ma; query zostaje `enabled:false`, lista pusta.
  const fieldsForModule = useFieldsForModule(
    parentEntity && parentEntity !== 'documents' ? `crm_core.${parentEntity}` : null,
  );
  const siblingFields = (fieldsForModule.data?.fields ?? []).map((f) => ({
    slug: f.slug,
    name: f.name,
  }));

  useEffect(() => {
    if (open) {
      setDisplayName(initialValues.displayName);
      setSlug(initialValues.slug);
      setSlugTouched(mode === 'edit');
      setParentEntity(initialValues.parentEntity);
      setDescription(initialValues.description);
      setSourceSql(initialValues.sourceSql);
      setDeclaredParams(initialValues.declaredParams);
      setParamBindings(initialValues.paramBindings);
      setTestParamValues({});
      testMutation.reset();
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (serverErrors) setErrors((prev) => ({ ...prev, ...serverErrors }));
  }, [serverErrors]);

  if (!open) return null;

  const resolvedSlug = slugTouched ? slug : nameToSlug(displayName);
  const isIdentityLocked = mode === 'edit';

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!displayName.trim()) {
      next.displayName = t('additionalViews.admin.form.errors.displayNameRequired');
    }
    if (!isIdentityLocked && !SLUG_REGEX.test(resolvedSlug)) {
      next.slug = t('additionalViews.admin.form.errors.slugInvalid');
    }
    if (!sourceSql.trim()) {
      next.sourceSql = t('additionalViews.admin.form.errors.sqlRequired');
    }
    const names = declaredParams.map((p) => p.name.trim());
    if (names.some((n) => !PARAM_NAME_REGEX.test(n))) {
      next.declaredParams = t('additionalViews.admin.form.errors.paramNameInvalid');
    } else if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) {
      next.declaredParams = t('additionalViews.admin.form.errors.paramNameDuplicate');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    const parent = SYSTEM_PARENT_OPTIONS.find((p) => p.parentEntity === parentEntity);
    if (!parent) return;
    onSubmit({
      displayName: displayName.trim(),
      slug: resolvedSlug,
      parentTableName: parent.parentTableName,
      parentEntityType: parent.parentEntityType,
      parentEntity: parent.parentEntity,
      description: description.trim(),
      sourceSql: sourceSql.trim(),
      declaredParams: declaredParams.map((p) => ({ ...p, name: p.name.trim() })),
      paramBindings,
    });
  }

  function updateParam(index: number, patch: Partial<AdditionalViewSourceParam>) {
    setDeclaredParams((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function removeParam(index: number) {
    const removedName = declaredParams[index]?.name;
    setDeclaredParams((prev) => prev.filter((_, i) => i !== index));
    // Wiązanie osieroconego parametru (usuniętego z deklaracji) nie ma już sensu — usuń je,
    // żeby `paramBindings` nie niosło kluczy spoza `declaredParams`.
    if (removedName && paramBindings && removedName in paramBindings) {
      const next = { ...paramBindings };
      delete next[removedName];
      setParamBindings(Object.keys(next).length > 0 ? next : null);
    }
  }

  function handleTest() {
    if (!sourceSql.trim()) return;
    const paramValues: Record<string, string> = {};
    for (const p of declaredParams) {
      const raw = testParamValues[p.name];
      if (raw !== undefined && raw !== '') paramValues[p.name] = raw;
    }
    testMutation.mutate({
      sql: sourceSql.trim(),
      declaredParams: declaredParams.map((p) => ({ ...p, name: p.name.trim() })),
      ...(Object.keys(paramValues).length > 0 ? { paramValues } : {}),
    });
  }

  const testResult = testMutation.data ?? null;
  const testErrorMessage = testMutation.isError
    ? (() => {
        const err = testMutation.error;
        const code =
          isAxiosError(err) && err.response?.data && typeof err.response.data === 'object'
            ? ((err.response.data as { error?: string }).error ?? null)
            : null;
        return code
          ? t('additionalViews.admin.form.testErrors.withCode', { code })
          : t('additionalViews.admin.form.testErrors.generic');
      })()
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Szerokie okno — dane widoku w dwóch kolumnach, pod nimi pole SQL na całą szerokość,
      // na dole parametry obok „Testuj” (bez przewijania).
      size="2xl"
      title={
        mode === 'create'
          ? t('additionalViews.admin.form.titleCreate')
          : t('additionalViews.admin.form.titleEdit')
      }
    >
      <div className="space-y-4" data-testid="additional-view-form-modal">
        <div
          className="grid grid-cols-1 items-start gap-x-6 gap-y-3 lg:grid-cols-2"
          data-testid="additional-view-form-layout"
        >
          <div className="space-y-3">
            <FormField
              label={t('additionalViews.admin.form.displayName')}
              error={errors.displayName}
              required
            >
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                data-testid="additional-view-form-display-name"
                autoFocus
              />
            </FormField>

            <FormField label={t('additionalViews.admin.form.description')}>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                data-testid="additional-view-form-description"
              />
            </FormField>
          </div>

          <div className="space-y-3">
            <FormField
              label={t('additionalViews.admin.form.slug')}
              hint={
                isIdentityLocked
                  ? t('additionalViews.admin.form.identityLockedHint')
                  : t('additionalViews.admin.form.slugHint')
              }
              error={errors.slug}
            >
              <Input
                value={resolvedSlug}
                disabled={isIdentityLocked}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugTouched(true);
                }}
                data-testid="additional-view-form-slug"
              />
            </FormField>

            <FormField label={t('additionalViews.admin.form.parent')} required>
              <Dropdown
                value={parentEntity}
                disabled={isIdentityLocked}
                onChange={(v) => setParentEntity(v)}
                options={SYSTEM_PARENT_OPTIONS.map((opt) => ({
                  value: opt.parentEntity,
                  label: t(opt.labelKey),
                }))}
                data-testid="additional-view-form-parent"
              />
            </FormField>
          </div>

          {/* Pole SQL na całą szerokość okna (obie kolumny). */}
          <div className="space-y-1 lg:col-span-2">
            <div className="flex justify-end">
              <InsertExampleButton
                currentValue={sourceSql}
                example={t('additionalViews.admin.form.sqlExample')}
                onInsert={setSourceSql}
                data-testid="additional-view-form-sql-example"
              />
            </div>
            <FormField
              label={t('additionalViews.admin.form.sqlLabel')}
              hint={t('additionalViews.admin.form.sqlHint')}
              error={errors.sourceSql}
            >
              <SqlField
                value={sourceSql}
                onChange={(value) => setSourceSql(value)}
                schema={sqlSchema}
                parameters={sqlParams}
                diagnostics={sqlDiagnostics}
                placeholder={t('common.examplePlaceholder', {
                  example: t('additionalViews.admin.form.sqlExample'),
                })}
                aria-label={t('additionalViews.admin.form.sqlLabel')}
                minHeight="200px"
                data-testid="additional-view-form-sql"
              />
            </FormField>
          </div>

          {/* Lewa kolumna: parametry + powiązania; prawa: „Testuj” z wynikami. */}
          <div className="space-y-3">
            <FormField
              label={t('additionalViews.admin.form.paramsTitle')}
              hint={t('additionalViews.admin.form.paramsHint')}
              error={errors.declaredParams}
            >
              <div className="space-y-2" data-testid="additional-view-form-params">
                {declaredParams.map((param, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={param.name}
                      onChange={(e) => updateParam(index, { name: e.target.value })}
                      placeholder={t('additionalViews.admin.form.paramNamePlaceholder')}
                      containerClassName="min-w-0 flex-1"
                      data-testid={`additional-view-form-param-name-${index}`}
                    />
                    <Dropdown
                      className="w-32 shrink-0"
                      value={param.type}
                      onChange={(v) => updateParam(index, { type: v as AdditionalViewParamType })}
                      options={ADDITIONAL_VIEW_PARAM_TYPES.map((type) => ({
                        value: type,
                        label: t(`admin.globalDictionaries.form.paramType.${type}`),
                      }))}
                      data-testid={`additional-view-form-param-type-${index}`}
                    />
                    <Checkbox
                      checked={param.required}
                      onChange={(e) => updateParam(index, { required: e.target.checked })}
                      label={t('additionalViews.admin.form.paramRequired')}
                      containerClassName="shrink-0"
                      data-testid={`additional-view-form-param-required-${index}`}
                    />
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => removeParam(index)}
                      aria-label={t('additionalViews.admin.form.paramRemove')}
                      data-testid={`additional-view-form-param-remove-${index}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </IconButton>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() =>
                    setDeclaredParams((prev) => [
                      ...prev,
                      { name: '', type: 'text', required: false },
                    ])
                  }
                  data-testid="additional-view-form-param-add"
                >
                  {t('additionalViews.admin.form.paramAdd')}
                </Button>
              </div>
            </FormField>

            {declaredParams.length > 0 && (
              <DictionaryParamBindingsEditor
                params={declaredParams}
                siblingFields={siblingFields}
                value={paramBindings ?? undefined}
                onChange={(bindings) =>
                  setParamBindings(bindings as AdditionalViewParamBindings | null)
                }
                data-testid="additional-view-form-bindings"
              />
            )}
          </div>

          {/* Sekcja „Testuj" — podgląd autora, bez zapisu (ADR-0087 §7). */}
          <div
            className="space-y-2 rounded-lg border border-border-primary bg-bg-secondary p-3"
            data-testid="additional-view-form-test-section"
          >
            <p className="text-sm font-medium text-text-primary">
              {t('additionalViews.admin.form.testTitle')}
            </p>
            <p className="text-xs text-text-tertiary">{t('additionalViews.admin.form.testHint')}</p>

            {declaredParams.length > 0 && (
              <div className="space-y-2">
                {declaredParams
                  .filter((p) => p.name.trim())
                  .map((p) => (
                    <FormField key={p.name} label={p.name}>
                      <Input
                        value={testParamValues[p.name] ?? ''}
                        onChange={(e) =>
                          setTestParamValues((prev) => ({ ...prev, [p.name]: e.target.value }))
                        }
                        data-testid={`additional-view-form-test-param-${p.name}`}
                      />
                    </FormField>
                  ))}
              </div>
            )}

            <Button
              variant="secondary"
              size="sm"
              onClick={handleTest}
              loading={testMutation.isPending}
              disabled={testMutation.isPending || sourceSql.trim().length === 0}
              data-testid="additional-view-form-test-btn"
            >
              {t('additionalViews.admin.form.testButton')}
            </Button>

            {testErrorMessage && (
              <p
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
                data-testid="additional-view-form-test-error"
              >
                {testErrorMessage}
              </p>
            )}

            {testResult && (
              <div
                className="space-y-2 rounded-md border border-border-primary bg-bg-primary p-2"
                data-testid="additional-view-form-test-result"
              >
                {testResult.rows.length === 0 ? (
                  <p className="text-sm text-text-tertiary">
                    {t('additionalViews.admin.form.testEmpty')}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          {testResult.columns.map((col) => (
                            <th key={col.name} className="px-2 py-1 text-left text-text-secondary">
                              {col.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {testResult.rows.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {testResult.columns.map((col, colIndex) => (
                              <td key={col.name} className="px-2 py-1 text-text-primary">
                                {formatCellValue(row[colIndex], col.displayType, t)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {testResult.truncated && (
                  <p className="text-xs text-text-muted">
                    {t('additionalViews.admin.form.testTruncated')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || sourceSql.trim().length === 0}
            loading={saving}
            data-testid="additional-view-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
